import { useState, useEffect, useCallback } from 'react'
import { Bell, RefreshCw, Clock, Wifi, WifiOff, AlertTriangle, Search, User, Loader2, Settings, LogOut, X, CheckCircle } from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'
import clsx from 'clsx'
import { useUIStore, useAlertStore } from '@/store'
import { useAuth } from '@/contexts/AuthContext'

export default function Header() {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showAlerts, setShowAlerts] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)

  const { user, logout } = useAuth()

  const {
    isLiveMode,
    setLiveMode,
    connectionStatus,
    addNotification
  } = useUIStore()

  const { alerts, acknowledgeAlert } = useAlertStore()
  const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged_at)
  const alertCount = unacknowledgedAlerts.length

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-dropdown]')) {
        setShowAlerts(false)
        setShowUserMenu(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    addNotification({
      type: 'info',
      title: 'Refreshing data',
      message: 'Fetching latest updates from server...',
      duration: 2000
    })

    // Trigger a page-wide refresh event
    window.dispatchEvent(new CustomEvent('refresh-data'))

    // Simulate refresh delay
    setTimeout(() => {
      setIsRefreshing(false)
      addNotification({
        type: 'success',
        title: 'Data refreshed',
        message: 'All panels updated with latest data',
        duration: 3000
      })
    }, 1500)
  }, [addNotification])

  // Handle alert acknowledgment
  const handleAcknowledgeAlert = useCallback((alertId: string) => {
    acknowledgeAlert(alertId, 'System Operator')
    addNotification({
      type: 'success',
      title: 'Alert acknowledged',
      duration: 2000
    })
  }, [acknowledgeAlert, addNotification])

  // Determine display status based on live mode and connection status
  const getStatusDisplay = () => {
    if (!isLiveMode) {
      return {
        label: 'PAUSED',
        color: 'bg-surface-800 text-surface-400',
        icon: WifiOff,
        pulse: false,
        spin: false,
        tooltip: 'Live updates paused. Click to resume.'
      }
    }

    if (connectionStatus.api === 'error' || connectionStatus.api === 'disconnected') {
      return {
        label: 'OFFLINE',
        color: 'bg-status-critical/10 text-status-critical',
        icon: AlertTriangle,
        pulse: false,
        spin: false,
        tooltip: connectionStatus.lastError || 'API connection lost. Server may be unavailable.'
      }
    }

    if (connectionStatus.api === 'connecting') {
      return {
        label: 'CONNECTING',
        color: 'bg-status-warning/10 text-status-warning',
        icon: Loader2,
        pulse: false,
        spin: true,
        tooltip: 'Establishing connection to server...'
      }
    }

    return {
      label: 'LIVE',
      color: 'bg-status-healthy/10 text-status-healthy',
      icon: Wifi,
      pulse: true,
      spin: false,
      tooltip: `Connected. Last check: ${connectionStatus.lastApiCheck ? format(new Date(connectionStatus.lastApiCheck), 'HH:mm:ss') : 'N/A'}`
    }
  }

  const statusDisplay = getStatusDisplay()
  const StatusIcon = statusDisplay.icon

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500'
      case 'high': return 'bg-amber-500'
      case 'medium': return 'bg-cyan-500'
      default: return 'bg-emerald-500'
    }
  }

  return (
    <header className="h-16 bg-surface-900 border-b border-surface-700 flex items-center justify-between px-4 relative z-50">
      {/* Left Section - Search */}
      <div className="flex items-center gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-500" size={18} />
          <input
            type="text"
            placeholder="Search assets, alerts, reports..."
            className="input pl-10 w-80 bg-surface-800"
          />
        </div>
      </div>

      {/* Right Section - Status & Actions */}
      <div className="flex items-center gap-4">
        {/* Live Status Indicator - Shows LIVE, PAUSED, OFFLINE, or CONNECTING */}
        <button
          onClick={() => setLiveMode(!isLiveMode)}
          className={clsx(
            'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
            statusDisplay.color
          )}
          title={statusDisplay.tooltip}
        >
          <StatusIcon size={16} className={statusDisplay.spin ? 'animate-spin' : ''} />
          <span>{statusDisplay.label}</span>
          {statusDisplay.pulse && (
            <span className="w-2 h-2 rounded-full bg-status-healthy animate-pulse" />
          )}
        </button>

        {/* Refresh Button */}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className={clsx(
            'p-2 rounded-lg transition-all',
            isRefreshing
              ? 'text-primary-400 bg-primary-500/10'
              : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
          )}
          title="Refresh data"
        >
          <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
        </button>

        {/* Alerts Dropdown */}
        <div className="relative" data-dropdown>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowAlerts(!showAlerts)
              setShowUserMenu(false)
            }}
            className={clsx(
              'relative p-2 rounded-lg transition-all',
              showAlerts
                ? 'text-primary-400 bg-primary-500/10'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800'
            )}
            title={`${alertCount} active alerts`}
          >
            <Bell size={18} />
            {alertCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold bg-status-critical text-white rounded-full animate-pulse">
                {alertCount > 9 ? '9+' : alertCount}
              </span>
            )}
          </button>

          {/* Alerts Panel */}
          {showAlerts && (
            <div className="absolute right-0 top-full mt-2 w-96 bg-surface-800 border border-surface-600 rounded-xl shadow-2xl overflow-hidden">
              <div className="p-3 border-b border-surface-700 flex items-center justify-between">
                <span className="font-semibold text-white">Active Alerts</span>
                <span className="text-xs text-surface-400">{alertCount} unacknowledged</span>
              </div>

              <div className="max-h-80 overflow-y-auto">
                {unacknowledgedAlerts.length === 0 ? (
                  <div className="p-6 text-center">
                    <CheckCircle size={32} className="mx-auto text-emerald-400 mb-2" />
                    <p className="text-surface-400 text-sm">No active alerts</p>
                    <p className="text-surface-500 text-xs mt-1">All systems operating normally</p>
                  </div>
                ) : (
                  unacknowledgedAlerts.slice(0, 5).map((alert) => (
                    <div
                      key={alert.id}
                      className="p-3 border-b border-surface-700/50 hover:bg-surface-700/30 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className={clsx('w-2 h-2 rounded-full mt-1.5', getSeverityColor(alert.severity))} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white text-sm truncate">{alert.message}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-surface-400">
                            <span className="font-mono">{alert.asset_id}</span>
                            <span>•</span>
                            <span>{formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAcknowledgeAlert(alert.id)
                          }}
                          className="p-1 rounded hover:bg-surface-600 text-surface-500 hover:text-surface-300"
                          title="Acknowledge"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {alertCount > 5 && (
                <div className="p-3 border-t border-surface-700 text-center">
                  <button className="text-sm text-primary-400 hover:text-primary-300">
                    View all {alertCount} alerts
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Time Display */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-surface-800 rounded-lg" title="Current system time">
          <Clock size={16} className="text-surface-500" />
          <span className="text-sm font-mono text-surface-300">
            {format(currentTime, 'HH:mm:ss')}
          </span>
          <span className="text-xs text-surface-500">
            {format(currentTime, 'MMM dd')}
          </span>
        </div>

        {/* User Menu Dropdown */}
        <div className="relative" data-dropdown>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowUserMenu(!showUserMenu)
              setShowAlerts(false)
            }}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all',
              showUserMenu ? 'bg-surface-700' : 'hover:bg-surface-800'
            )}
            title="User profile and settings"
          >
            <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center">
              <User size={16} className="text-white" />
            </div>
          </button>

          {/* User Menu Panel */}
          {showUserMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-surface-800 border border-surface-600 rounded-xl shadow-2xl overflow-hidden">
              <div className="p-3 border-b border-surface-700">
                <p className="font-semibold text-white">{user?.name || 'Fleet Manager'}</p>
                <p className="text-xs text-surface-400">{user?.email || 'user@fleetmanager.com'}</p>
              </div>

              <div className="p-1">
                <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-surface-300 hover:bg-surface-700 hover:text-white transition-colors text-left">
                  <Settings size={16} />
                  <span className="text-sm">Settings</span>
                </button>
                <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-surface-300 hover:bg-surface-700 hover:text-white transition-colors text-left">
                  <User size={16} />
                  <span className="text-sm">Profile</span>
                </button>
              </div>

              <div className="p-1 border-t border-surface-700">
                <button
                  onClick={() => {
                    setShowUserMenu(false)
                    logout()
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors text-left"
                >
                  <LogOut size={16} />
                  <span className="text-sm">Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
